import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, XCircle, AlertTriangle, Info, X } from 'lucide-react';
import { cn } from '../../lib/cn';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastData {
  id: string;
  type: ToastType;
  title: string;
  description?: string;
  duration?: number;
}

interface ToastProps extends ToastData {
  onDismiss: (id: string) => void;
}

const iconMap = {
  success: CheckCircle2,
  error: XCircle,
  warning: AlertTriangle,
  info: Info,
};

const styleMap = {
  success: {
    container: 'bg-profit-container border-profit/20',
    icon: 'text-profit',
    title: 'text-profit',
  },
  error: {
    container: 'bg-loss-container border-loss/20',
    icon: 'text-loss',
    title: 'text-loss',
  },
  warning: {
    container: 'bg-status-pending-container border-status-pending/20',
    icon: 'text-status-pending',
    title: 'text-status-pending',
  },
  info: {
    container: 'bg-action-container border-action/20',
    icon: 'text-action',
    title: 'text-action',
  },
};

/**
 * Individual toast notification.
 */
export function Toast({ id, type, title, description, duration = 4000, onDismiss }: ToastProps) {
  const [isVisible, setIsVisible] = useState(true);
  const Icon = iconMap[type];
  const styles = styleMap[type];

  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        setIsVisible(false);
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [duration]);

  // Dismiss after exit animation
  useEffect(() => {
    if (!isVisible) {
      const timer = setTimeout(() => onDismiss(id), 150);
      return () => clearTimeout(timer);
    }
  }, [isVisible, id, onDismiss]);

  return (
    <motion.div
      initial={{ opacity: 0, y: -16, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -16, scale: 0.95 }}
      transition={{ duration: 0.15, ease: 'easeOut' }}
      className={cn(
        'relative flex items-start gap-3 p-3 pr-9',
        'rounded-lg border shadow-card',
        'backdrop-blur-sm',
        styles.container
      )}
      role="alert"
      aria-live="polite"
    >
      <Icon size={18} className={cn('flex-shrink-0 mt-0.5', styles.icon)} />

      <div className="flex-1 min-w-0">
        <p className={cn('text-body-sm font-medium', styles.title)}>{title}</p>
        {description && (
          <p className="text-body-sm text-text-secondary mt-0.5">
            {description}
          </p>
        )}
      </div>

      <button
        onClick={() => setIsVisible(false)}
        className={cn(
          'absolute top-2.5 right-2.5 p-1 rounded-full',
          'text-text-muted hover:text-text-secondary',
          'transition-colors'
        )}
        aria-label="Dismiss"
      >
        <X size={14} />
      </button>
    </motion.div>
  );
}

interface ToastContainerProps {
  toasts: ToastData[];
  onDismiss: (id: string) => void;
}

/**
 * Container for stacking toasts.
 */
export function ToastContainer({ toasts, onDismiss }: ToastContainerProps) {
  return (
    <div
      className={cn(
        'fixed top-4 right-4 z-50',
        'flex flex-col gap-2',
        'max-w-sm w-full',
        'pointer-events-none'
      )}
    >
      <AnimatePresence mode="popLayout">
        {toasts.map((toast) => (
          <div key={toast.id} className="pointer-events-auto">
            <Toast {...toast} onDismiss={onDismiss} />
          </div>
        ))}
      </AnimatePresence>
    </div>
  );
}
